<template>
  <div 
    class="kanban-card" 
    :class="{
      'contact-card': isContactCard,
      'conversation-card': isConversationCard,
      'company-card': isCompanyCard,
      'is-stagnant': card.is_stagnant
    }"
    :style="cardStyle"
    @click="handleCardClick"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchCancel"
    draggable="true"
    @dragstart="onDragStart($event)"
    @dragend="onDragEnd($event)"
  >
    <div class="card-header">
      <div class="header-content">
        <div class="avatar-container">
          <img v-if="card.contact_avatar" :src="card.contact_avatar" alt="Avatar" class="avatar">
          <div v-else class="avatar avatar-placeholder">
            {{ getInitials(card.title) }}
          </div>
        </div>
        <div class="title-container">
          <h4 class="card-title">{{ card.title }}</h4>
          <a
            :href="subtitleHref"
            class="card-subtitle subtitle-link copyable-item"
            @click.stop
          >
            <fluent-icon :icon="cardTypeIcon" size="12" class="icon" />
            <span class="copyable-text">
              {{ cardTypeLabel }}
            </span>
            <fluent-icon icon="open" size="12" class="icon" />
          </a>
        </div>
      </div>
    </div>

    <div class="card-content">
      <div v-if="isConversationCard && card.inbox_name" class="info-item">
        <fluent-icon icon="inbox" size="12" class="icon" />
        <span>{{ card.inbox_name }}</span>
      </div>

      <!-- Exibe última mensagem para conversas -->
      <div v-if="formattedLastMessage" class="card-message">
        <fluent-icon icon="chat" size="12" class="icon card-message-icon" />
        <span
          v-tooltip.top="lastMessageTooltip"
          class="card-message-text"
        >
          {{ formattedLastMessage }}
        </span>
      </div>
      
      <!-- Exibe biografia para contatos -->
      <p v-if="isContactCard && card.bio" class="card-message">
        <span class="card-message-text">{{ card.bio }}</span>
      </p>

      <div class="card-info">
        <div v-if="card.contact_email" class="info-item copyable-item" @click="copyToClipboard($event, card.contact_email, 'email')" :title="$t('KANBAN.CARD.CLIPBOARD.CLICK_TO_COPY', { value: card.contact_email })">
          <fluent-icon icon="mail" size="12" class="icon" />
          <span class="copyable-text">{{ card.contact_email }}</span>
        </div>
        <div v-if="card.contact_phone" class="info-item copyable-item" @click="copyToClipboard($event, card.contact_phone, 'phone')" :title="$t('KANBAN.CARD.CLIPBOARD.CLICK_TO_COPY', { value: card.contact_phone })">
          <fluent-icon icon="phone" size="12" class="icon" />
          <span class="copyable-text">{{ card.contact_phone }}</span>
        </div>

        <!-- Seção de Campos Personalizados -->
        <div v-if="attributesToShow.length" class="custom-attributes-container">
          <transition-group name="slide-fade" tag="div">
            <div
              v-for="attr in displayedAttributes"
              :key="attr.name"
              class="info-item attribute-item copyable-item"
              :title="$t('KANBAN.CARD.CLIPBOARD.CLICK_TO_COPY', { value: attr.copyValue })"
              @click="copyToClipboard($event, attr.copyValue, 'attribute')"
            >
              <fluent-icon :icon="attr.icon" size="12" class="icon" />
              <span class="attribute-label">{{ attr.name }}:</span>
              <span class="copyable-text">{{ attr.value }}</span>
            </div>
          </transition-group>
          <div v-if="hasMoreAttributes" class="w-full">
            <button
              @click="toggleAttributes"
              class="text-xs text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-500 font-medium"
            >
              {{ showAllAttributes ? $t('KANBAN.CARD.SHOW_LESS') : $t('KANBAN.CARD.SHOW_MORE') }}
            </button>
          </div>
        </div>
        
        <div v-if="hasAssignmentInfo" class="card-assignment-footer">
          <div v-if="hasAssignee" class="info-item assignment-item">
            <fluent-icon icon="person" size="12" class="icon" />
            <span>
              {{ $t('KANBAN.CARD.ASSIGNEE') }}:
              {{ card.assignee_name }}
            </span>
          </div>
          <div v-if="hasTeam" class="info-item assignment-item">
            <fluent-icon icon="people-team" size="12" class="icon" />
            <span>
              {{ $t('KANBAN.CARD.TEAM') }}:
              {{ card.team_name }}
            </span>
          </div>
        </div>
      </div>

      <div class="card-footer">
        <div class="card-meta">
          <span class="card-date">
            <fluent-icon icon="calendar" size="12" class="icon" />
            {{ formatDate(displayDate) }}
          </span>
        </div>
        <div class="card-badge" :class="cardStatusClass" v-if="isConversationCard && card.status">
          {{ getStatusText(card.status) }}
        </div>
      </div>

      <!-- Adicionar novos indicadores -->
      <div class="card-indicators">
        <!-- Indicador de tarefas -->
        <span 
          v-if="card.tasks_count > 0" 
          class="indicator task-indicator"
          :class="{ 'has-overdue': card.has_overdue_tasks }"
          @click="openTasksPage"
        >
          <fluent-icon icon="clipboard-task-list-outline" size="14" class="icon mr-1" />
          {{ card.tasks_count }}
        </span>
        
        <!-- Indicador de notas -->
        <span 
          v-if="card.notes_count > 0" 
          class="indicator note-indicator"
          @click="openNotesPage"
        >
          <fluent-icon icon="notepad-solid" size="14" class="icon mr-1" />
          {{ card.notes_count }}
        </span>
        
        <!-- Novo indicador de estagnação -->
        <span 
          v-if="shouldShowStagnationBadge" 
          class="indicator stagnation-info-indicator"
          :class="stagnationBadgeClass"
          :title="stagnationTooltip"
        >
          <fluent-icon icon="clock" size="14" class="icon mr-1" />
          {{ stagnationText }}
        </span>
        
        <!-- Indicador de estagnação (legado - mantido para compatibilidade apenas quando não há regra configurada) -->
        <span 
          v-if="card.is_stagnant && !shouldShowStagnationBadge" 
          class="indicator stagnant-indicator"
        >
          <fluent-icon icon="clock" size="14" class="icon mr-1" />
          {{ $t('KANBAN.STAGNANT_FOR_DAYS', { days: card.stagnant_for_days }) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import { utcToZonedTime } from 'date-fns-tz';
import { DEFAULT_ACCOUNT_TIMEZONE } from 'dashboard/constants/account';

export default {
  name: 'KanbanCard',
  props: {
    card: {
      type: Object,
      required: true
    },
    labelId: {
      type: [Number, String],
      required: true
    },
    columnColor: {
      type: String,
      default: null
    },
    funnelContactAttributeIds: {
      type: Array,
      default: () => [],
    },
    funnelConversationAttributeIds: {
      type: Array,
      default: () => [],
    }
  },
  data() {
    return {
      showAllAttributes: false,
      touchDragTimerId: null,
      touchStartPoint: null,
      touchDragStarted: false,
      suppressCardClick: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters.getCurrentUser || {};
    },
    accountTimezone() {
      return this.$store.getters.getCurrentAccount?.timezone || DEFAULT_ACCOUNT_TIMEZONE;
    },
    isContactCard() {
      return this.card.type === 'contact';
    },
    isCompanyCard() {
      return this.card.type === 'company';
    },
    isConversationCard() {
      return !this.isContactCard && !this.isCompanyCard;
    },
    cardTypeIcon() {
      if (this.isContactCard) return 'person';
      if (this.isCompanyCard) return 'briefcase';
      return 'chat';
    },
    cardTypeLabel() {
      if (this.isContactCard) return this.$t('KANBAN.CARD.CONTACT');
      if (this.isCompanyCard) return this.$t('KANBAN.CARD.COMPANY');
      return this.$t('KANBAN.CARD.CONVERSATION');
    },
    allAttributes() {
      const getter = this.$store.getters['attributes/getAttributesByModel'];
      if (!getter) return [];
      if (this.isContactCard) {
        return getter('contact_attribute');
      }
      if (this.isCompanyCard) {
        return getter('company_attribute');
      }
      return getter('conversation_attribute');
    },
    cardStatusClass() {
      if (!this.card.status) return '';
      return `status-${this.card.status}`;
    },
    cardStyle() {
      // Cria um estilo combinando a cor da coluna com o tipo de card
      const baseColor = this.isContactCard
        ? '#4CAF50'
        : this.isCompanyCard
          ? '#F59E0B'
          : '#2196F3';
      const columnColor = this.columnColor || '#1f93ff';
      
      return {
        borderLeftColor: baseColor,
        '--column-color': columnColor
      };
    },
    displayDate() {
      // Garantir que a data de última atividade tenha prioridade
      const lastActivity = this.card.last_activity_at;
      const created = this.card.created_at;
      
      // Usar a data mais recente disponível
      return lastActivity || created;
    },
    subtitleHref() {
      const accountId = this.$route.params.accountId;

      if (this.isContactCard) {
        return frontendURL(`accounts/${accountId}/contacts/${this.card.contact_id}`);
      }

      if (this.isCompanyCard) {
        return frontendURL(`accounts/${accountId}/companies/${this.card.company_id}`);
      }

      return frontendURL(`accounts/${accountId}/conversations/${this.card.id}`);
    },
    shouldShowStagnationBadge() {
      // Mostrar apenas se houver regra de estagnação configurada
      return this.card.stagnation_rule_days && this.card.stagnation_rule_days > 0;
    },
    stagnationBadgeClass() {
      if (!this.shouldShowStagnationBadge) return '';
      
      const stagnationInfo = this.getStagnationInfo();
      
      if (stagnationInfo.isStagnant) {
        return 'is-stagnant';
      } else if (stagnationInfo.daysUntilStagnation <= 1) {
        return 'is-near-stagnant';
      } else {
        return 'is-normal';
      }
    },
    stagnationTooltip() {
      if (!this.shouldShowStagnationBadge) return '';
      
      const stagnationInfo = this.getStagnationInfo();
      
      if (stagnationInfo.isStagnant) {
        if (stagnationInfo.stagnantForDays === 0) {
          const dayWord = this.$t(`KANBAN.CARD.STAGNATION.DAY_${this.card.stagnation_rule_days === 1 ? 'SINGULAR' : 'PLURAL'}`);
          return this.$t('KANBAN.CARD.STAGNATION.TODAY', { 
            days: this.card.stagnation_rule_days, 
            dayWord: dayWord 
          });
        }
        const stagnantDayWord = this.$t(`KANBAN.CARD.STAGNATION.DAY_${stagnationInfo.stagnantForDays === 1 ? 'SINGULAR' : 'PLURAL'}`);
        const ruleDayWord = this.$t(`KANBAN.CARD.STAGNATION.DAY_${this.card.stagnation_rule_days === 1 ? 'SINGULAR' : 'PLURAL'}`);
        return this.$t('KANBAN.CARD.STAGNATION.FOR_DAYS', { 
          stagnantDays: stagnationInfo.stagnantForDays,
          stagnantDayWord: stagnantDayWord,
          ruleDays: this.card.stagnation_rule_days,
          ruleDayWord: ruleDayWord
        });
      } else {
        const stagnationDate = this.formatStagnationDate(stagnationInfo.stagnationDate);
        const untilDayWord = this.$t(`KANBAN.CARD.STAGNATION.DAY_${stagnationInfo.daysUntilStagnation === 1 ? 'SINGULAR' : 'PLURAL'}`);
        const ruleDayWord = this.$t(`KANBAN.CARD.STAGNATION.DAY_${this.card.stagnation_rule_days === 1 ? 'SINGULAR' : 'PLURAL'}`);
        return this.$t('KANBAN.CARD.STAGNATION.WILL_STAGNATE', { 
          date: stagnationDate,
          untilDays: stagnationInfo.daysUntilStagnation,
          untilDayWord: untilDayWord,
          ruleDays: this.card.stagnation_rule_days,
          ruleDayWord: ruleDayWord
        });
      }
    },
    stagnationText() {
      if (!this.shouldShowStagnationBadge) return '';
      
      const stagnationInfo = this.getStagnationInfo();
      
      if (stagnationInfo.isStagnant) {
        if (stagnationInfo.stagnantForDays === 0) {
          return '0d';
        }
        return `+${stagnationInfo.stagnantForDays}d`;
      } else {
        return `${stagnationInfo.daysUntilStagnation}d`;
      }
    },
    attributesToShow() {
      const cardAttributes = this.card.custom_attributes || {};
      if (Object.keys(cardAttributes).length === 0) {
        return [];
      }

      const funnelAttributeIds = this.isContactCard
        ? this.funnelContactAttributeIds
        : this.funnelConversationAttributeIds;

      if (!funnelAttributeIds || funnelAttributeIds.length === 0) {
        return [];
      }

      const funnelAttributeDefinitions = this.allAttributes.filter(attr =>
        funnelAttributeIds.includes(attr.id)
      );

      return funnelAttributeDefinitions
        .map(attrDef => {
          const value = cardAttributes[attrDef.attribute_key];

          if (value !== undefined && value !== null && value !== '') {
            let formattedValue = value;
            if (attrDef.attribute_display_type === 'date') {
              formattedValue = this.formatAttributeDate(value);
            } else if (attrDef.attribute_display_type === 'checkbox') {
              formattedValue = value
                ? this.$t('KANBAN.CUSTOM_ATTRIBUTES.BOOLEAN_VALUES.TRUE')
                : this.$t('KANBAN.CUSTOM_ATTRIBUTES.BOOLEAN_VALUES.FALSE');
            }

            const iconMap = {
              text: 'text',
              number: 'number-symbol',
              currency: 'currency',
              percent: 'percent',
              link: 'link',
              date: 'calendar',
              list: 'list',
              checkbox: 'checkmark-square-solid',
            };

            return {
              name: attrDef.attribute_display_name,
              value: formattedValue,
              copyValue: String(formattedValue),
              icon: iconMap[attrDef.attribute_display_type] || 'code-outline',
            };
          }
          return null;
        })
        .filter(Boolean);
    },
    displayedAttributes() {
      if (this.showAllAttributes) {
        return this.attributesToShow;
      }
      return this.attributesToShow.slice(0, 2);
    },
    hasMoreAttributes() {
      return this.attributesToShow.length > 2;
    },
    hasAssignee() {
      return this.card.assignee_id !== null && this.card.assignee_id !== undefined
        ? true
        : Boolean(this.card.assignee_name);
    },
    hasTeam() {
      return this.card.team_id !== null && this.card.team_id !== undefined
        ? true
        : Boolean(this.card.team_name);
    },
    hasAssignmentInfo() {
      return this.hasAssignee || this.hasTeam;
    },
    lastMessageAuthorLabel() {
      if (this.isContactCard || this.isCompanyCard || !this.card.last_message) {
        return '';
      }

      const {
        last_message_sender_id: senderId,
        last_message_sender_type: senderType,
        last_message_sender_name: senderName,
        title,
      } = this.card;

      if (senderType === 'User') {
        if (Number(senderId) === Number(this.currentUser.id)) {
          return this.$t('KANBAN.CARD.ME');
        }

        return senderName || this.$t('KANBAN.CARD.ASSIGNEE');
      }

      return title || this.$t('KANBAN.CARD.CONTACT');
    },
    formattedLastMessage() {
      if (this.isContactCard || this.isCompanyCard || !this.card.last_message) {
        return '';
      }

      return `(${this.lastMessageAuthorLabel}): ${this.card.last_message}`;
    },
    lastMessageTooltip() {
      if (!this.formattedLastMessage) {
        return null;
      }

      return {
        content: this.formattedLastMessage,
        classes: 'kanban-message-tooltip',
        delay: { show: 150, hide: 0 },
      };
    }
  },
  beforeDestroy() {
    this.clearTouchDragState();
  },
  methods: {
    handleCardClick() {
      if (this.suppressCardClick) {
        this.suppressCardClick = false;
        return;
      }

      this.$emit('card-click', this.card);
    },
    onTouchStart(event) {
      if (!event.touches || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      const cardElement = event.currentTarget;
      this.clearTouchDragState();
      this.touchStartPoint = {
        x: touch.clientX,
        y: touch.clientY,
      };

      this.touchDragTimerId = window.setTimeout(() => {
        if (!this.touchStartPoint || !cardElement) {
          return;
        }

        const rect = cardElement.getBoundingClientRect();
        this.touchDragStarted = true;
        this.suppressCardClick = true;
        this.$emit('touch-drag-start', {
          card: this.card,
          labelId: this.labelId,
          html: cardElement.outerHTML,
          width: rect.width,
          height: rect.height,
          offsetX: rect.width / 2,
          offsetY: Math.min(48, rect.height / 4),
          x: this.touchStartPoint.x,
          y: this.touchStartPoint.y,
        });
      }, 180);
    },
    onTouchMove(event) {
      if (!event.touches || !event.touches.length || !this.touchStartPoint) {
        return;
      }

      const touch = event.touches[0];
      const deltaX = Math.abs(touch.clientX - this.touchStartPoint.x);
      const deltaY = Math.abs(touch.clientY - this.touchStartPoint.y);

      if (!this.touchDragStarted && (deltaX > 8 || deltaY > 8)) {
        this.clearTouchDragTimer();
        this.touchStartPoint = null;
        return;
      }

      if (this.touchDragStarted) {
        event.preventDefault();
        this.$emit('touch-drag-move', {
          x: touch.clientX,
          y: touch.clientY,
        });
      }
    },
    onTouchEnd(event) {
      const touch = event.changedTouches && event.changedTouches[0];

      if (this.touchDragStarted) {
        if (event.cancelable) {
          event.preventDefault();
        }

        this.suppressCardClick = true;
        this.$emit('touch-drag-end', {
          x: touch ? touch.clientX : this.touchStartPoint?.x,
          y: touch ? touch.clientY : this.touchStartPoint?.y,
        });
      }

      this.clearTouchDragState();
    },
    onTouchCancel() {
      if (this.touchDragStarted) {
        this.suppressCardClick = true;
        this.$emit('touch-drag-cancel');
      }

      this.clearTouchDragState();
    },
    clearTouchDragTimer() {
      if (this.touchDragTimerId) {
        window.clearTimeout(this.touchDragTimerId);
        this.touchDragTimerId = null;
      }
    },
    clearTouchDragState() {
      this.clearTouchDragTimer();
      this.touchStartPoint = null;
      this.touchDragStarted = false;
    },
    toggleAttributes(event) {
      // Previne que o clique no botão abra o card
      if (event) {
        event.stopPropagation();
      }
      this.showAllAttributes = !this.showAllAttributes;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      
      try {
        const date = this.toAccountDate(dateString);
        
        // Verificar se é uma data válida
        if (Number.isNaN(date.getTime())) {
          return '';
        }
        
        const now = utcToZonedTime(new Date(), this.accountTimezone);
        const isToday = this.localDateKey(date) === this.localDateKey(now);
        
        if (isToday) {
          // Para datas de hoje, mostrar a hora no formato local
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        
        return date.toLocaleDateString([], { 
          day: 'numeric', 
          month: 'short'
        });
      } catch (error) {
        return '';
      }
    },
    toAccountDate(value) {
      return utcToZonedTime(value instanceof Date ? value : new Date(value), this.accountTimezone);
    },
    localDateKey(date) {
      return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    },
    formatAttributeDate(value) {
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-');
        return `${day}/${month}/${year}`;
      }

      const date = this.toAccountDate(value);
      return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('pt-BR');
    },
    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    getStatusText(status) {
      return this.$t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${status}.TEXT`);
    },
    onDragStart(event) {
      event.dataTransfer.setData('application/json', JSON.stringify({
        cardId: this.card.id,
        sourceLabelId: this.labelId,
        cardType: this.card.type || (this.card.id.toString().startsWith('contact_') ? 'contact' : this.card.id.toString().startsWith('company_') ? 'company' : 'conversation'),
        cardData: this.card
      }));
      
      event.dataTransfer.setData('text/plain', this.card.id);
      
      event.dataTransfer.effectAllowed = 'move';
      
      this.$emit('dragstart', event, this.card, this.labelId);
    },
    onDragEnd(event) {
      this.$emit('dragend', event);
    },
    openTasksPage(event) {
      // Previne propagação do evento de clique do card
      event.stopPropagation();
      
      if (this.card.type === 'contact') {
        // Para contatos, navega para a página do contato com a aba de tarefas ativa (index 1)
        const accountId = this.$route.params.accountId;
        this.$router.push({
          name: 'contact_profile_dashboard',
          params: { 
            accountId: accountId,
            contactId: this.card.contact_id 
          },
          query: { tab: '1' } // índice 1 = aba de tarefas no ContactManageView
        });
      } else if (this.card.type === 'conversation') {
        // Para conversas, navega para os detalhes da conversa com a aba de tarefas
        const accountId = this.$route.params.accountId;
        this.$router.push({ 
          name: 'inbox_conversation',
          params: { 
            accountId: accountId,
            conversation_id: this.card.conversation_id 
          },
          query: { tab: 'tasks' }
        });
      } else if (this.card.type === 'company') {
        const accountId = this.$route.params.accountId;
        this.$router.push({
          name: 'company_profile_dashboard',
          params: {
            accountId,
            companyId: this.card.company_id,
          },
          query: { tab: 'tasks' },
        });
      }
    },
    openNotesPage(event) {
      // Previne propagação do evento de clique do card
      event.stopPropagation();
      
      if (this.card.type === 'contact') {
        // Para contatos, navega para a página do contato com a aba de notas ativa (index 0)
        const accountId = this.$route.params.accountId;
        this.$router.push({
          name: 'contact_profile_dashboard',
          params: { 
            accountId: accountId,
            contactId: this.card.contact_id 
          },
          query: { tab: '0' } // índice 0 = aba de notas no ContactManageView
        });
      } else if (this.card.type === 'conversation') {
        // Para conversas, navega para os detalhes da conversa com a aba de mensagens/notas
        const accountId = this.$route.params.accountId;
        this.$router.push({ 
          name: 'inbox_conversation',
          params: { 
            accountId: accountId,
            conversation_id: this.card.conversation_id 
          },
          query: { tab: 'messages' } // notas ficam no contexto de mensagens para conversas
        });
      } else if (this.card.type === 'company') {
        const accountId = this.$route.params.accountId;
        this.$router.push({
          name: 'company_profile_dashboard',
          params: {
            accountId,
            companyId: this.card.company_id,
          },
          query: { tab: 'notes' },
        });
      }
    },
    getStagnationInfo() {
      // Obter a data de última atividade ou criação do card
      const lastActivity = this.card.last_activity_at || this.card.created_at;
      if (!lastActivity || !this.card.stagnation_rule_days) {
        return {
          isStagnant: false,
          stagnantForDays: 0,
          daysUntilStagnation: 0,
          stagnationDate: null
        };
      }

      const lastActivityDate = this.toAccountDate(lastActivity);
      const now = utcToZonedTime(new Date(), this.accountTimezone);
      
      // Calcular quantos dias se passaram desde a última atividade
      const daysSinceLastActivity = Math.floor(
        (this.localDateKey(now) - this.localDateKey(lastActivityDate)) / (1000 * 60 * 60 * 24)
      );
      
      // Calcular a data de estagnação (última atividade + dias para estagnação)
      const stagnationDate = new Date(
        lastActivityDate.getFullYear(),
        lastActivityDate.getMonth(),
        lastActivityDate.getDate()
      );
      stagnationDate.setDate(lastActivityDate.getDate() + this.card.stagnation_rule_days);
      
      // Calcular dias até a estagnação (pode ser negativo se já estagnado)
      const daysUntilStagnation = Math.ceil(
        (this.localDateKey(stagnationDate) - this.localDateKey(now)) / (1000 * 60 * 60 * 24)
      );
      
      // Determinar se está estagnado
      const isStagnant = daysSinceLastActivity >= this.card.stagnation_rule_days;
      
      // CORREÇÃO: Calcular corretamente há quantos dias está estagnado
      // Se a regra é 5 dias e se passaram 5 dias = estagnado há 0 dias (acabou de estagnar)
      // Se a regra é 5 dias e se passaram 6 dias = estagnado há 1 dia
      const stagnantForDays = isStagnant ? Math.max(0, daysSinceLastActivity - this.card.stagnation_rule_days) : 0;

      return {
        isStagnant,
        stagnantForDays,
        daysUntilStagnation: Math.max(0, daysUntilStagnation), // Não mostrar valores negativos
        stagnationDate
      };
    },
    formatStagnationDate(date) {
      if (!date) return '';
      
      return date.toLocaleDateString([], { 
        day: 'numeric', 
        month: 'short'
      });
    },
    async copyToClipboard(event, text, type) {
      // Previne propagação do evento de clique do card
      event.stopPropagation();
      
      try {
        await navigator.clipboard.writeText(text);
        
        // Determina a mensagem baseada no tipo
        const message = this.getClipboardSuccessMessage(type);
        
        // Mostra mensagem de sucesso usando o sistema de alertas do app
        useAlert(message);
        
      } catch (error) {
        
        // Fallback para navegadores antigos usando document.execCommand
        try {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (successful) {
            const message = this.getClipboardSuccessMessage(type);
            useAlert(message);
          } else {
            throw new Error('Fallback copy failed');
          }
          
        } catch (fallbackError) {
          useAlert(this.$t('KANBAN.CARD.CLIPBOARD.COPY_ERROR'));
        }
      }
    },
    getClipboardSuccessMessage(type) {
      if (type === 'email') {
        return this.$t('KANBAN.CARD.CLIPBOARD.EMAIL_COPIED');
      }

      if (type === 'phone') {
        return this.$t('KANBAN.CARD.CLIPBOARD.PHONE_COPIED');
      }

      return this.$t('KANBAN.CARD.CLIPBOARD.ATTRIBUTE_COPIED');
    }
  },
  watch: {
    'card.last_activity_at': {
      immediate: true,
      handler() {
        // Força a reavaliação da exibição quando a data de atividade muda
        this.$forceUpdate();
      }
    }
  }
};
</script>

<style scoped>
.kanban-card {
  background-color: rgb(var(--fd-surface-hover));
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 4px solid transparent;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kanban-card:hover {
  transform: translateY(-3px);
}

/* Destacando mais a barra superior */
.kanban-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background-color: var(--column-color, rgb(var(--color-flipdesk-500)));
  opacity: 1;
}

.contact-card {
  border-left-color: rgb(var(--color-green-600)); /* Verde para contatos */
}

.conversation-card {
  border-left-color: rgb(var(--color-flipdesk-500)); /* Azul para conversas */
}

.company-card {
  border-left-color: rgb(var(--color-amber-500));
}

.dark .kanban-card {
  background-color: rgb(var(--fd-surface));  /* Cor de fundo para tema escuro */
}




.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-container {
  flex-shrink: 0;
}

.avatar {
  width: 36px;
  height: 36px;
  object-fit: cover;
  @apply rounded-avatar;
}

.avatar-placeholder {
  background-color: rgb(var(--fd-surface-hover));
  color: rgb(var(--fd-muted));
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.title-container {
  min-width: 0;
  overflow: visible;
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgb(var(--fd-body));
}

.card-subtitle {
  font-size: 12px;
  color: rgb(var(--fd-body));
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-weight: 500;
}

.card-subtitle svg {
  color: black;
}

.subtitle-link {
  gap: 4px;
  width: fit-content;
  text-decoration: none;
}

.card-content {
  color: rgb(var(--fd-body));
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-message {
  font-size: 13px;
  color: rgb(var(--fd-body));
  margin: 0;
  padding: 2px 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.card-message-text {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  font-weight: 500;
  min-width: 0;
  width: 100%;
}

.card-message-icon {
  flex-shrink: 0;
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.custom-attributes-container,
.custom-attributes-container > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: rgb(var(--fd-body));
  margin-bottom: 0;
  font-weight: 500;
  padding: 2px 4px;
  border-radius: 4px;
  width: fit-content;
  max-width: 100%;
}

.info-item svg {
  color: black;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 4px;
}

.card-assignment-footer {
  margin-top: 0;
  padding-top: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.assignment-item {
  margin-bottom: 0;
}

.card-meta {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgb(var(--fd-body));
  font-weight: 500;
  margin-top: 0;
}

.card-date {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgb(var(--fd-body));
}

.card-date .icon {
  color: rgb(var(--fd-body));
}

.card-badge {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 12px;
  width: fit-content;
}

.status-open {
  background-color: rgb(var(--color-green-50));
  color: rgb(var(--fd-body));
  font-weight: 600;
}

.status-pending {
  background-color: rgb(var(--color-amber-50));
  color: rgb(var(--color-amber-800));
  font-weight: 600;
}

.status-resolved {
  background-color: rgb(var(--color-flipdesk-100));
  color: rgb(var(--fd-muted));
  font-weight: 600;
}

.status-snoozed {
  background-color: rgb(var(--fd-surface-hover));
  color: rgb(var(--fd-muted));
  font-weight: 600;
}

.icon {
  color: rgb(var(--fd-muted));
}

.mr-1 {
  margin-right: 4px;
}

.card-indicators {
  display: flex;
  gap: 4px;
  margin-top: 0;
  flex-wrap: wrap;
  color: rgb(var(--fd-body));
  padding: 2px 4px;
}

.indicator {
  display: flex;
  align-items: center;
  font-size: 12px;
  padding: 2px 4px;
  border-radius: 4px;
  background-color: rgb(var(--fd-surface-raised));
  cursor: pointer;
}

.card-indicators .icon {
  color: rgb(var(--fd-body));
}

.task-indicator {
  background-color: rgb(var(--fd-surface-raised));
}

.task-indicator.has-overdue {
  background-color: rgb(var(--color-ruby-500));
}

.stagnant-indicator {
  background-color: rgb(var(--color-ruby-500));
  color: white;
}

.stagnation-info-indicator {
  cursor: default;
}

.stagnation-info-indicator.is-normal {
  background-color: rgb(var(--color-green-600));
  color: white;
}

.stagnation-info-indicator.is-near-stagnant {
  background-color: rgb(var(--color-amber-500));
  color: white;
}

.stagnation-info-indicator.is-stagnant {
  background-color: rgb(var(--color-ruby-500));
  color: white;
}

.kanban-card[draggable="true"] {
  cursor: grab;
}

.kanban-card[draggable="true"]:active {
  cursor: grabbing;
  opacity: 0.6;
}

.is-stagnant {
  background-color: rgb(var(--color-ruby-500) / 0.1);
  border-left: 3px solid rgb(var(--color-ruby-500));
}

/* Tema escuro */
@media (prefers-color-scheme: dark) {
  .avatar-placeholder {
    background-color: rgb(var(--fd-surface-raised));
    color: rgb(var(--fd-body));
  }
  
  .dark .card-title {
    color: rgb(var(--fd-body));
  }
  
  .dark .card-subtitle, 
  .dark .info-item, 
  .dark .icon, 
  .dark .card-date, 
  .dark .indicator, 
  .dark .card-message {
    color: rgb(var(--fd-body));
  }
  
  .dark .card-badge {
    background: rgb(var(--fd-surface-raised));
    color: rgb(var(--fd-body));
  }
  
  .dark .status-open {
    background-color: rgb(var(--fd-surface-raised) / 0.2);
    color: rgb(var(--fd-subtle));
  }
  
  .dark .status-pending {
    background-color: rgb(var(--color-amber-500) / 0.2);
    color: rgb(var(--fd-subtle));
  }
  
  .dark .status-resolved {
    background-color: rgb(var(--color-flipdesk-500) / 0.2);
    color: rgb(var(--fd-subtle));
  }
  
  .dark .status-snoozed {
    background-color: rgb(var(--fd-surface-raised) / 0.2);
    color: rgb(var(--fd-subtle));
  }
  
  .dark .stagnation-info-indicator.is-normal {
    background-color: rgb(var(--color-green-600));
    color: rgb(var(--fd-body));
  }
  
  .dark .stagnation-info-indicator.is-near-stagnant {
    background-color: rgb(var(--color-amber-500));
    color: rgb(var(--fd-body));
  }
  
  .dark .stagnation-info-indicator.is-stagnant {
    background-color: rgb(var(--color-ruby-500));
    color: rgb(var(--fd-body));
  }
}

/* Estilos para campos copiáveis */
.copyable-item {
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px 4px;
  border-radius: 4px;
  position: relative;
}

.copyable-item:hover {
  background-color: rgb(var(--color-flipdesk-500) / 0.1);
  color: rgb(var(--fd-brand-text));
  transform: none;
}

.copyable-item:hover .icon {
  color: rgb(var(--fd-brand-text));
}

.copyable-item:hover .copyable-text {
  color: rgb(var(--fd-brand-text));
  font-weight: 600;
}

.copyable-item:active {
  transform: scale(0.98);
  background-color: rgb(var(--color-flipdesk-500) / 0.2);
}

/* Animação sutil para feedback visual */
.copyable-item::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.copyable-item:hover::after {
  border-color: rgb(var(--color-flipdesk-500) / 0.3);
}

/* Tema escuro para campos copiáveis */
.dark .copyable-item:hover {
  background-color: rgb(var(--color-flipdesk-500) / 0.2);
  color: rgb(var(--fd-brand-text));
}

.dark .copyable-item:hover .icon {
  color: rgb(var(--fd-brand-text));
}

.dark .copyable-item:hover .copyable-text {
  color: rgb(var(--fd-brand-text));
}

.dark .copyable-item:active {
  background-color: rgb(var(--color-flipdesk-500) / 0.3);
}
</style>
